import React, { useEffect, useState } from "react";
import {
  getShapeGeometry,
  isShapeTool,
  pointsToSvgPath,
  HIGHLIGHT_TOOLS,
} from "../../utils/highlightCoords";

const strokeStyle = (stroke, { filled = false } = {}) => {
  const isHighlighter = stroke.tool === HIGHLIGHT_TOOLS.HIGHLIGHTER;
  return {
    fill: filled ? stroke.color : "none",
    fillOpacity: filled ? 0.2 : undefined,
    stroke: stroke.color,
    strokeWidth: stroke.width,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    opacity: isHighlighter ? 0.45 : 1,
    style: { mixBlendMode: isHighlighter || filled ? "multiply" : "normal" },
  };
};

function ShapeMark({ stroke, width, height }) {
  const geom = getShapeGeometry(stroke, width, height);
  if (!geom) return null;
  const filled = geom.type === "rect" || geom.type === "ellipse";
  const props = strokeStyle(stroke, { filled });
  if (geom.type === "rect") {
    return (
      <rect
        x={geom.x}
        y={geom.y}
        width={geom.width}
        height={geom.height}
        {...props}
      />
    );
  }
  if (geom.type === "ellipse") {
    return (
      <ellipse
        cx={geom.cx}
        cy={geom.cy}
        rx={geom.rx}
        ry={geom.ry}
        {...props}
      />
    );
  }
  if (geom.type === "line") {
    return (
      <line
        x1={geom.x1}
        y1={geom.y1}
        x2={geom.x2}
        y2={geom.y2}
        {...props}
      />
    );
  }
  if (geom.type === "path") {
    return <path d={geom.d} {...props} />;
  }
  return null;
}

function StrokePath({ stroke, width, height }) {
  const d = pointsToSvgPath(stroke.points, width, height);
  if (!d) return null;
  return <path d={d} {...strokeStyle(stroke)} />;
}

function StrokeMark({ stroke, width, height }) {
  if (isShapeTool(stroke.tool)) {
    return <ShapeMark stroke={stroke} width={width} height={height} />;
  }
  return <StrokePath stroke={stroke} width={width} height={height} />;
}

export default function HighlightOverlay({
  overlayRef,
  strokes,
  liveStroke,
  laser,
  interactive,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
}) {
  const [size, setSize] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  }));

  useEffect(() => {
    const node = overlayRef?.current;
    if (!node) return undefined;
    const update = () => {
      const rect = node.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [overlayRef]);

  const allStrokes = liveStroke ? [...strokes, liveStroke] : strokes;
  const { width, height } = size;

  return (
    <svg
      ref={overlayRef}
      className="screen-highlight-overlay"
      width="100%"
      height="100%"
      onPointerDown={interactive ? onPointerDown : undefined}
      onPointerMove={interactive ? onPointerMove : undefined}
      onPointerUp={interactive ? onPointerUp : undefined}
      onPointerLeave={interactive ? onPointerLeave : undefined}
    >
      {width > 0 &&
        allStrokes.map((stroke) => (
          <StrokeMark key={stroke.id} stroke={stroke} width={width} height={height} />
        ))}
      {laser && width > 0 ? (
        <circle
          className="screen-highlight-laser"
          cx={laser.x * width}
          cy={laser.y * height}
          r={14}
          fill="#ff3b30"
          opacity="0.75"
        />
      ) : null}
    </svg>
  );
}
